import { useCallback } from 'react';

import type { InviteUserResponse, OnSubmitData } from '@atlassian/invite-people/types';
import OriginTracing from '@atlassiansox/origin-tracing/origin-tracer';
import type { IEnvironment } from 'relay-runtime';
import { graphql, createOperationDescriptor, getRequest } from 'relay-runtime';

import { useAnalytics } from '@townsquare/analytics';
import { resolvedAvailableSitesProvider } from '@townsquare/available-sites-provider/provider';
import { getAggRelayEnvironment } from '@townsquare/relay-utils/agg-environment';

import { JiraRoleDetail, JiraSpaceOption } from '../types/SpaceInputTypes';
import { hasJiraProducts } from '../utils/hasJiraProducts';
import { isBulkInviteSuccessResponse } from '../utils/isBulkInviteSuccessResponse';

import type { useOnSubmitQuery$data } from './__generated__/useOnSubmitQuery.graphql';
import { mapOfferingNameToEdition } from './mapOfferingNameToEdition';
import { useHandleInviteFlags } from './useHandleInviteFlags';
import { useInviteToProject } from './useInviteToProject';
import type { InviteAnalyticsContext, InviteToProjectResult } from './useInviteToProject';

const isValidQueryData = (data: unknown): data is useOnSubmitQuery$data => {
	return data !== null && typeof data === 'object' && 'tenantContexts' in data && 'jira' in data;
};

const ANALYTICS_SOURCE = 'peopleDirectoryHomeAddPeopleModal';

const getJiraSiteUrl = async (cloudId: string): Promise<string | undefined> => {
	try {
		const result = await resolvedAvailableSitesProvider.getAvailableProducts({
			cloudIdsToFilterFor: [cloudId],
			orgId: '',
		});
		return result.data.find((site) => site.cloudId === cloudId)?.url;
	} catch {
		return undefined;
	}
};

const getProjectContinueUrl = (
	jiraSiteUrl: string | undefined,
	selectedSpace: JiraSpaceOption,
): string => {
	if (jiraSiteUrl) {
		return `${jiraSiteUrl.replace(/\/$/, '')}/browse/${selectedSpace.key}`;
	}
	return window.location.href;
};

/**
 * Resolves the Jira project invite for the given users.
 * Returns null for projectResult when inviting to a project is not applicable.
 */
const tryJiraProjectInvite = async (
	submitData: OnSubmitData,
	cloudId: string,
	selectedSpace: JiraSpaceOption | undefined,
	selectedRole: JiraRoleDetail | undefined,
	deps: {
		environment: IEnvironment;
		inviteToProject: ReturnType<typeof useInviteToProject>;
		analytics: ReturnType<typeof useAnalytics>;
	},
): Promise<{ projectResult: InviteToProjectResult | null; isSiteAdmin: boolean }> => {
	const { environment, inviteToProject, analytics } = deps;
	const { users, selectedProducts } = submitData.invitePeopleData;

	if (!hasJiraProducts(selectedProducts) || !selectedSpace || users.length === 0) {
		return { projectResult: null, isSiteAdmin: false };
	}

	const operation = createOperationDescriptor(
		getRequest(graphql`
			query useOnSubmitQuery($cloudId: ID!) {
				tenantContexts(cloudIds: [$cloudId]) {
					entitlementInfo(hamsProductKey: "jira-software") {
						entitlement {
							offering {
								name
							}
						}
					}
				}
				jira {
					isGlobalAdmin: hasGlobalPermission(cloudId: $cloudId, key: ADMINISTER)
						@optIn(to: "JiraHasGlobalPermission")
				}
			}
		`),
		{ cloudId },
	);
	const snapshot = environment.lookup(operation.fragment);

	if (snapshot.isMissingData || !isValidQueryData(snapshot.data)) {
		void analytics.track('projectUpdate', 'failed', {
			source: ANALYTICS_SOURCE,
			reason: 'missingSnapshotData',
		});
		return { projectResult: null, isSiteAdmin: false };
	}

	const isSiteAdmin = !!snapshot.data.jira?.isGlobalAdmin;
	const offering = snapshot.data.tenantContexts?.[0]?.entitlementInfo?.entitlement?.offering?.name;
	const isFreeEdition = mapOfferingNameToEdition(offering) === 'FREE_EDITION';

	const atlOrigin = new OriginTracing({ product: 'teams' });
	const jiraSiteUrl = await getJiraSiteUrl(cloudId);
	const analyticsContext: InviteAnalyticsContext = {
		source: ANALYTICS_SOURCE,
		roleId: selectedRole?.id,
		projectId: selectedSpace.id,
		spaceSource: selectedSpace.source,
		spaceType: selectedSpace?.projectTypeKey,
		isDefaultRole: selectedRole?.default,
		isAdminRole: selectedRole?.admin,
		isAdmin: isSiteAdmin,
		atlOriginAttributes: atlOrigin.toAnalyticsAttributes({ hasGeneratedId: true }),
	};

	const projectResult = await inviteToProject(
		users,
		{
			cloudId,
			selectedSpace,
			selectedRole,
			atlOrigin: atlOrigin.encode(),
			continueUrl: getProjectContinueUrl(jiraSiteUrl, selectedSpace),
			getRecaptchaToken: submitData.getRecaptchaToken,
		},
		isFreeEdition,
		analyticsContext,
	);

	const { invitedCount, requestedCount } = projectResult.successfulInvites.reduce(
		(acc, { response }) => {
			if (response.inviteStatus === 'INVITED' || response.inviteStatus === 'USER_EXISTS')
				acc.invitedCount++;
			else if (response.inviteStatus === 'INVITED_PENDING_APPROVAL') acc.requestedCount++;
			return acc;
		},
		{ invitedCount: 0, requestedCount: 0 },
	);

	if (invitedCount > 0 || requestedCount > 0) {
		void analytics.track('project', 'updated', {
			...analyticsContext,
			numberOfUsersInvitedByEmail: invitedCount,
			numberOfUsersRequestedAccess: requestedCount,
			numberOfInvitesFailed: projectResult.failedInvites.length,
			totalRequests: users.length,
		});
	} else {
		void analytics.track('projectUpdate', 'failed', {
			...analyticsContext,
		});
	}

	return { projectResult, isSiteAdmin };
};

/**
 * Builds the analytics attributes for the addPeopleSubmit complete event.
 */
export const getTrackingAttributes: (
	projectResult: InviteToProjectResult | null,
	inviteUsersResponse: InviteUserResponse | null,
	totalUsersSubmitted: number,
	anomalyFailStatuses: string[] | null,
	anomalyFailMessages: string[] | null,
) => {
	totalUsersSubmitted: number;
	projectInviteSuccessCount: number | null;
	projectInviteFailureCount: number | null;
	projectFailureStatuses:
		| (import('../clients/JiraRestClient').InviteStatus | 'GENERIC_ERROR')[]
		| null;
	productInviteSuccessCount: number | null;
	productInviteFailureCount: number | null;
	anomalyFailStatuses: string[] | null;
	anomalyFailMessages: string[] | null;
} = (
	projectResult: InviteToProjectResult | null,
	inviteUsersResponse: InviteUserResponse | null,
	totalUsersSubmitted: number,
	anomalyFailStatuses: string[] | null,
	anomalyFailMessages: string[] | null,
) => {
	const successResponse =
		inviteUsersResponse?.status === 'fulfilled' &&
		inviteUsersResponse.value.responseStatus === 'success' &&
		isBulkInviteSuccessResponse(inviteUsersResponse.value.response)
			? inviteUsersResponse.value.response
			: null;

	let productInviteSuccessCount: number | null = null;
	let productInviteFailureCount: number | null = null;

	if (successResponse) {
		productInviteSuccessCount =
			successResponse.invited.length + successResponse.accessRequested.length;
		productInviteFailureCount = successResponse.error.length;
	} else if (inviteUsersResponse) {
		/*
		InviteUserResponse is PromiseSettledResult<InviteProcessingResponse<BulkInviteResponse, unknown>>, so the else if branch is reached when successResponse is null — meaning one of these cases:
		1. 'rejected': No — promise rejected entirely
		2. 'fulfilled' + 'error': No — exception thrown, no users processed
		3. 'fulfilled' + 'skipped': No — invite skipped entirely
		4. 'fulfilled' + 'success' + BulkInviteFailureResponse: No — has failure field, entire batch failed

		In all four cases, no users were successfully invited, so productInviteSuccessCount = 0 is correct.
		The only way to get partial success is status === 'fulfilled' + responseStatus === 'success' + BulkInviteSuccessResponse (the invited/accessRequested/error shape), which is handled by the if (successResponse) branch above.
		 */
		productInviteSuccessCount = 0;
		productInviteFailureCount = totalUsersSubmitted;
	}

	return {
		totalUsersSubmitted,
		projectInviteSuccessCount: projectResult?.successfulInvites.length ?? null,
		projectInviteFailureCount: projectResult?.failedInvites.length ?? null,
		projectFailureStatuses:
			projectResult?.failedInvites.map((f) => f.status ?? 'GENERIC_ERROR') ?? null,
		productInviteSuccessCount,
		productInviteFailureCount,
		anomalyFailStatuses,
		anomalyFailMessages,
	};
};

export const useOnSubmit: (
	selectedSpace: JiraSpaceOption | undefined,
	selectedRole: JiraRoleDetail | undefined,
	cloudId: string | null | undefined,
	onComplete: () => void,
) => (submitData: OnSubmitData) => Promise<void> = (
	selectedSpace: JiraSpaceOption | undefined,
	selectedRole: JiraRoleDetail | undefined,
	cloudId: string | null | undefined,
	onComplete: () => void,
) => {
	const environment = getAggRelayEnvironment();
	const inviteToProject = useInviteToProject();
	const handleInviteFlags = useHandleInviteFlags();
	const analytics = useAnalytics();

	const handleSubmit = useCallback(
		async (submitData: OnSubmitData) => {
			if (!cloudId) {
				return;
			}

			const { users } = submitData.invitePeopleData;

			// --- Step 1: Jira project invite (when a space is selected) ---
			const { projectResult, isSiteAdmin } = await tryJiraProjectInvite(
				submitData,
				cloudId,
				selectedSpace,
				selectedRole,
				{ environment, inviteToProject, analytics },
			);

			// --- Step 2: Product-level invite (always runs) ---
			const { inviteUsersResponse } = await submitData.defaultOnSubmit();

			// --- Step 3: Flag display ---
			const { anomalyFailStatuses, anomalyFailMessages } = handleInviteFlags(
				projectResult,
				inviteUsersResponse,
				isSiteAdmin,
			);

			void analytics.track(
				'teamAddPeopleSubmit',
				'complete',
				getTrackingAttributes(
					projectResult,
					inviteUsersResponse,
					users.length,
					anomalyFailStatuses,
					anomalyFailMessages,
				),
			);

			onComplete();
		},
		[
			cloudId,
			selectedSpace,
			selectedRole,
			environment,
			onComplete,
			inviteToProject,
			handleInviteFlags,
			analytics,
		],
	);

	return handleSubmit;
};
